import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  CheckCircle2, ChevronRight, Plus, Trash2, GripVertical, Upload,
  BookOpen, Layers, BarChart3, Video, FileText, Radio, Music,
  ArrowLeft, Info, ArrowUp, ArrowDown, PenLine, X, Check, ChevronDown, Volume2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createCourse,
  createChapter,
  createLesson,
  CourseTypeBackend,
  LessonTypeBackend,
  uploadCourseThumbnail,
  uploadLessonVideo,
  uploadLessonPdf,
  uploadLessonAudio,
  type UiCourseType,
  type UiLessonType,
} from "@/api/lms/courses";
import { examConfigApi } from "@/api/admin/examConfig";

const steps = [
  {
    label: "Basic Info",
    description: "Title, description & thumbnail",
    icon: BookOpen,
  },
  {
    label: "Structure",
    description: "Chapters and lessons",
    icon: Layers,
  },
  {
    label: "Pricing",
    description: "Price and publish settings",
    icon: BarChart3,
  },
];

interface Lesson {
  id: string;
  title: string;
  type: UiLessonType;
  duration: string;
  isPreview?: boolean;
  description?: string;
  objectives?: string;
  contentUrl?: string;
  contentKey?: string;
  liveStartAt?: string | null;
  liveEndAt?: string | null;
}

interface Chapter {
  id: string;
  title: string;
  lessons: Lesson[];
}

// --- Helpers & Subcomponents for Course Structure Builder ---

function formatUiDuration(durationStr: string): string {
  if (!durationStr) return "";
  const trimmed = durationStr.trim();
  const num = Number.parseFloat(trimmed);
  if (Number.isFinite(num)) {
    return `${Math.round(num / 60)}m`;
  }
  return trimmed;
}

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function ThemedFilePicker({
  accept,
  disabled,
  onPick,
  label = "Choose file",
}: {
  accept: string;
  disabled?: boolean;
  onPick: (file: File) => void;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.currentTarget.value = "";
          if (file) onPick(file);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        className="shrink-0 gap-1.5"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-3.5 w-3.5 shrink-0" />
        {label}
      </Button>
    </>
  );
}

function lessonTypeIcon(type: UiLessonType, size = 14) {
  if (type === "video") return <Video size={size} className="text-primary shrink-0" />;
  if (type === "pdf") return <FileText size={size} className="text-amber-500 shrink-0" />;
  if (type === "audio") return <Volume2 size={size} className="text-emerald-500 shrink-0" />;
  return <Radio size={size} className="text-violet-500 shrink-0" />;
}

function lessonTypeBadge(type: UiLessonType) {
  const map: Record<string, string> = {
    video: "bg-primary/10 text-primary",
    pdf: "bg-amber-50 text-amber-600",
    audio: "bg-emerald-50 text-emerald-600",
    live: "bg-violet-50 text-violet-600",
  };
  return map[type] ?? "bg-secondary text-muted-foreground";
}

function LessonRow({
  lesson,
  index,
  isLast,
  onDelete,
  onMoveUp,
  onMoveDown,
  onUpdate,
}: {
  lesson: Lesson;
  index: number;
  isLast: boolean;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onUpdate: (updated: Partial<Lesson>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<Lesson | null>(null);

  const effectiveLesson = draft ?? lesson;

  const handleSave = () => {
    onUpdate(effectiveLesson);
    setDraft(null);
    setExpanded(false);
  };

  const handleCancel = () => {
    setDraft(null);
    setExpanded(false);
  };

  const [isUploading, setIsUploading] = useState(false);

  const handlePickFile = async (file: File) => {
    setIsUploading(true);
    try {
      const type = effectiveLesson.type;
      let uploaded;
      if (type === "video") {
        uploaded = await uploadLessonVideo(file);
      } else if (type === "pdf") {
        uploaded = await uploadLessonPdf(file);
      } else if (type === "audio") {
        uploaded = await uploadLessonAudio(file);
      }
      if (uploaded) {
        setDraft({
          ...effectiveLesson,
          contentUrl: uploaded.url,
          contentKey: uploaded.key,
        });
        toast.success("File uploaded successfully");
      }
    } catch (err: any) {
      toast.error(err?.message || "File upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className={cn(
        "group rounded-lg border border-border/60 bg-card overflow-hidden transition-all duration-150",
        expanded && "shadow-sm",
      )}
    >
      {/* Row header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        {lessonTypeIcon(effectiveLesson.type)}
        <span className="flex-1 text-sm font-medium text-foreground truncate">{effectiveLesson.title}</span>
        {effectiveLesson.isPreview && (
          <span className="text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full shrink-0">
            Preview
          </span>
        )}
        {effectiveLesson.duration && (
          <span className="text-[11px] text-muted-foreground shrink-0">
            {formatUiDuration(effectiveLesson.duration)}
          </span>
        )}
        <span className={cn(
          "text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0",
          lessonTypeBadge(effectiveLesson.type),
        )}>
          {effectiveLesson.type.toUpperCase()}
        </span>
        {/* Up/Down reorder buttons */}
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 p-1 rounded transition-colors"
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp();
            }}
            title="Move up"
          >
            <ArrowUp size={11} />
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 p-1 rounded transition-colors"
            disabled={isLast}
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown();
            }}
            title="Move down"
          >
            <ArrowDown size={11} />
          </button>
        </div>

        <button
          onClick={() => {
            setDraft(lesson);
            setExpanded(!expanded);
          }}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
          title="Edit lesson"
        >
          <PenLine size={13} />
        </button>
        <button
          onClick={onDelete}
          className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded opacity-0 group-hover:opacity-100"
        >
          <Trash2 size={13} />
        </button>
      </div>

      {/* Expanded edit form */}
      {expanded && (
        <div className="border-t border-border/60 p-4 bg-muted/20 space-y-4">
          <div className="flex flex-wrap items-start gap-3">
            {/* Title */}
            <div className="flex-1 min-w-[160px] space-y-1">
              <Label className="text-xs">Title</Label>
              <Input
                value={effectiveLesson.title}
                onChange={(e) => setDraft({ ...effectiveLesson, title: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            {/* Type */}
            <div className="space-y-1 w-28">
              <Label className="text-xs">Type</Label>
              <select
                value={effectiveLesson.type}
                onChange={(e) => setDraft({ ...effectiveLesson, type: e.target.value as UiLessonType })}
                className="h-8 w-full px-2 rounded-md border border-border bg-background text-xs text-foreground"
              >
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
                <option value="audio">Audio</option>
                <option value="live">Live</option>
              </select>
            </div>
            {/* Duration */}
            <div className="space-y-1 w-28">
              <Label className="text-xs">Duration (sec)</Label>
              <Input
                type="number"
                value={effectiveLesson.duration ? effectiveLesson.duration.replace(/[^0-9.]/g, "") : ""}
                onChange={(e) => setDraft({ ...effectiveLesson, duration: e.target.value })}
                placeholder="e.g. 300"
                className="h-8 text-xs"
              />
            </div>
            {/* Preview toggle */}
            <div className="flex items-center gap-2 pt-6">
              <Switch
                id={`preview-${lesson.id}`}
                checked={Boolean(effectiveLesson.isPreview)}
                onCheckedChange={(checked) => setDraft({ ...effectiveLesson, isPreview: checked })}
              />
              <Label htmlFor={`preview-${lesson.id}`} className="text-xs cursor-pointer select-none">
                Free Preview (No Login Required)
              </Label>
            </div>
          </div>

          {/* Description and Objectives */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Description / Summary</Label>
              <textarea
                value={effectiveLesson.description ?? ""}
                onChange={(e) => setDraft({ ...effectiveLesson, description: e.target.value })}
                placeholder="Lesson overview..."
                rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Learning Objectives (one per line)</Label>
              <textarea
                value={effectiveLesson.objectives ?? ""}
                onChange={(e) => setDraft({ ...effectiveLesson, objectives: e.target.value })}
                placeholder="e.g. Understand core concepts&#10;Practice grammar structures..."
                rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          {/* File upload / URL */}
          {(effectiveLesson.type === "video" || effectiveLesson.type === "pdf" || effectiveLesson.type === "audio") && (
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-border/60 bg-background">
              <ThemedFilePicker
                accept={
                  effectiveLesson.type === "video" ? "video/*"
                    : effectiveLesson.type === "pdf" ? "application/pdf"
                      : "audio/*"
                }
                disabled={isUploading}
                onPick={handlePickFile}
                label={isUploading ? "Uploading…" : "Upload file"}
              />
              {effectiveLesson.contentUrl && (
                <a
                  href={effectiveLesson.contentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-primary underline truncate max-w-[200px]"
                >
                  View current file ↗
                </a>
              )}
              {!effectiveLesson.contentUrl && !isUploading && (
                <span className="text-xs text-muted-foreground">No file uploaded yet</span>
              )}
            </div>
          )}

          {effectiveLesson.type === "live" && (
            <div className="space-y-3 p-3 rounded-lg border border-border/60 bg-background">
              <div className="space-y-1.5">
                <Label className="text-xs">Meeting Link</Label>
                <Input
                  value={effectiveLesson.contentUrl ?? ""}
                  onChange={(e) => setDraft({ ...effectiveLesson, contentUrl: e.target.value })}
                  placeholder="https://meet.google.com/… or Zoom link"
                  className="text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Start time</Label>
                  <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(effectiveLesson.liveStartAt)}
                    onChange={(e) => setDraft({ ...effectiveLesson, liveStartAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">End time</Label>
                  <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(effectiveLesson.liveEndAt)}
                    onChange={(e) => setDraft({ ...effectiveLesson, liveEndAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} className="gap-1.5">
              <Check size={13} />
              Save Lesson
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChapterCard({
  chapter,
  index,
  isLast,
  onDeleteChapter,
  onMoveUp,
  onMoveDown,
  onUpdateTitle,
  onAddLesson,
  onDeleteLesson,
  onUpdateLesson,
  onMoveLessonUp,
  onMoveLessonDown,
}: {
  chapter: Chapter;
  index: number;
  isLast: boolean;
  onDeleteChapter: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onUpdateTitle: (title: string) => void;
  onAddLesson: () => void;
  onDeleteLesson: (lessonId: string) => void;
  onUpdateLesson: (lessonId: string, updated: Partial<Lesson>) => void;
  onMoveLessonUp: (idx: number) => void;
  onMoveLessonDown: (idx: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(chapter.title);

  const handleSaveTitle = () => {
    onUpdateTitle(titleDraft.trim() || chapter.title);
    setEditingTitle(false);
  };

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm">
      {/* Chapter header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60 bg-muted/20 rounded-t-2xl">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronDown
            size={15}
            className={cn("transition-transform duration-200", collapsed && "-rotate-90")}
          />
        </button>

        {/* Chapter title */}
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveTitle();
                  if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(chapter.title); }
                }}
                className="h-7 text-sm font-semibold"
                autoFocus
              />
              <Button size="sm" className="h-7 px-2" onClick={handleSaveTitle}>
                <Check size={13} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => { setEditingTitle(false); setTitleDraft(chapter.title); }}
              >
                <X size={13} />
              </Button>
            </div>
          ) : (
            <button
              className="flex items-center gap-2 group/title"
              onClick={() => { setEditingTitle(true); setTitleDraft(chapter.title); }}
            >
              <span className="text-sm font-semibold text-foreground">{chapter.title}</span>
              <PenLine size={12} className="text-muted-foreground opacity-0 group-hover/title:opacity-100 transition-opacity" />
            </button>
          )}
        </div>

        {/* Chapter meta */}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground shrink-0">
          <span>{chapter.lessons.length} lesson{chapter.lessons.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Up/Down reorder buttons */}
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground hover:text-foreground rounded-md"
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp();
            }}
            title="Move up"
          >
            <ArrowUp size={12} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground hover:text-foreground rounded-md"
            disabled={isLast}
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown();
            }}
            title="Move down"
          >
            <ArrowDown size={12} />
          </Button>
        </div>

        <button
          onClick={onDeleteChapter}
          className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Chapter content */}
      {!collapsed && (
        <div className="p-4 space-y-2">
          {chapter.lessons.map((l, lessonIndex) => (
            <LessonRow
              key={l.id}
              lesson={l}
              index={lessonIndex}
              isLast={lessonIndex === chapter.lessons.length - 1}
              onDelete={() => onDeleteLesson(l.id)}
              onMoveUp={() => onMoveLessonUp(lessonIndex)}
              onMoveDown={() => onMoveLessonDown(lessonIndex)}
              onUpdate={(updated) => onUpdateLesson(l.id, updated)}
            />
          ))}

          {chapter.lessons.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No content yet. Use <span className="font-medium text-foreground">+ Add Content</span> to get started.
            </p>
          )}

          {/* Add Content button */}
          <div className="pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-primary hover:bg-primary/10 text-xs font-medium"
              onClick={onAddLesson}
            >
              <Plus size={14} />
              Add Content
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CreateCoursePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    courseType: "recorded" as UiCourseType,
    selectedLevels: ["N3"],
    price: "" as number | "",
    chapters: [
      {
        id: "1",
        title: "Chapter 1: Introduction",
        lessons: [{ id: "1-1", title: "Welcome", type: "video" as UiLessonType, duration: "10 min" }]
      },
    ] as Chapter[],
    published: false,
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement | null>(null);

  const { title, description, courseType, selectedLevels, price, chapters, published } = formData;

  const updateFormData = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const { data: activeConfigs = [] } = useQuery({
    queryKey: ["activeExamConfigs"],
    queryFn: () => examConfigApi.getActive(),
  });



  const parseDurationToSeconds = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const lower = trimmed.toLowerCase();
    const num = Number.parseFloat(trimmed.replace(/[^0-9.]/g, ""));
    if (Number.isNaN(num)) return undefined;
    if (lower.includes("min")) return Math.max(0, Math.round(num * 60));
    if (lower.includes("sec")) return Math.max(0, Math.round(num));
    return Math.max(0, Math.round(num));
  };

  const uploadThumbnailMutation = useMutation({
    mutationFn: (file: File) => uploadCourseThumbnail(file),
    onSuccess: (data) => {
      setThumbnailUrl(data.url);
      toast.success("Thumbnail uploaded");
    },
    onError: (err: any) => {
      const msg = err?.message || err?.body?.message || "Thumbnail upload failed";
      toast.error(msg);
      setThumbnailFile(null);
      setThumbnailUrl(null);
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
        setThumbnailPreviewUrl(null);
      }
    },
  });

  const createCourseMutation = useMutation({
    mutationFn: async (publish: boolean) => {
      if (!title.trim()) throw new Error("Course title is required");
      if (chapters.length === 0) throw new Error("Add at least one chapter");

      const typeBackend = courseType === "live" ? CourseTypeBackend.live : CourseTypeBackend.recorded;

      let thumbnailToUse: string | undefined = thumbnailUrl ?? undefined;

      if (!thumbnailToUse && thumbnailFile) {
        const uploaded = await uploadCourseThumbnail(thumbnailFile);
        thumbnailToUse = uploaded.url;
        setThumbnailUrl(uploaded.url);
      }

      const createdCourse = await createCourse({
        title: title.trim(),
        description: description.trim() || undefined,
        levels: selectedLevels.length ? selectedLevels : undefined,
        type: typeBackend,
        price: typeof price === "number" ? price : undefined,
        thumbnail: thumbnailToUse,
        isPublished: publish,
      });

      const courseId = createdCourse.id;

      for (let i = 0; i < chapters.length; i++) {
        const ch = chapters[i];
        const createdChapter = await createChapter(courseId, {
          title: ch.title.trim() || `Chapter ${i + 1}`,
          order: i,
        });

        for (let j = 0; j < ch.lessons.length; j++) {
          const lesson = ch.lessons[j];
          const durationSeconds = parseDurationToSeconds(lesson.duration);
          const lessonTypeBackend = LessonTypeBackend[lesson.type];

          await createLesson(courseId, createdChapter.id, {
            title: lesson.title.trim() || `Lesson ${j + 1}`,
            type: lessonTypeBackend,
            duration: durationSeconds,
            order: j,
            contentUrl: lesson.contentUrl || undefined,
            contentKey: lesson.contentKey || undefined,
            liveStartAt: lesson.liveStartAt || undefined,
            liveEndAt: lesson.liveEndAt || undefined,
            isPreview: lesson.isPreview || undefined,
            description: lesson.description || undefined,
            objectives: lesson.objectives || undefined,
          });
        }
      }
      return createdCourse;
    },
    onSuccess: (createdCourse) => {
      toast.success("Course created! Opening the Course Builder…");
      // Redirect straight to the Course Builder
      navigate(`/admin/courses/${createdCourse.id}/structure`);
    },
    onError: (err: any) => {
      const msg = err?.message || err?.body?.message || "Failed to create course";
      toast.error(msg);
    },
  });

  const onPickThumbnailFile = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file for the thumbnail");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Thumbnail is too large (max 2MB)");
      return;
    }
    if (thumbnailPreviewUrl) URL.revokeObjectURL(thumbnailPreviewUrl);
    const preview = URL.createObjectURL(file);
    setThumbnailPreviewUrl(preview);
    setThumbnailFile(file);
    setThumbnailUrl(null);
    uploadThumbnailMutation.mutate(file);
  };

  const addChapter = () => {
    updateFormData({
      chapters: [...chapters, { id: Date.now().toString(), title: `Chapter ${chapters.length + 1}`, lessons: [] }]
    });
  };

  const addLesson = (chapterId: string) => {
    updateFormData({
      chapters: chapters.map(ch =>
        ch.id === chapterId
          ? { ...ch, lessons: [...ch.lessons, { id: Date.now().toString(), title: `Lesson ${ch.lessons.length + 1}`, type: "video" as UiLessonType, duration: "" }] }
          : ch
      )
    });
  };

  const removeChapter = (id: string) => updateFormData({ chapters: chapters.filter(c => c.id !== id) });

  const removeLesson = (chId: string, lId: string) => {
    updateFormData({
      chapters: chapters.map(ch => ch.id === chId ? { ...ch, lessons: ch.lessons.filter(l => l.id !== lId) } : ch)
    });
  };

  const handleMoveChapter = (idx: number, direction: "up" | "down") => {
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= chapters.length) return;

    const reordered = [...chapters];
    const temp = reordered[idx];
    reordered[idx] = reordered[targetIdx];
    reordered[targetIdx] = temp;
    updateFormData({ chapters: reordered });
  };

  const handleMoveLesson = (chapterId: string, idx: number, direction: "up" | "down") => {
    const ch = chapters.find(c => c.id === chapterId);
    if (!ch) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= ch.lessons.length) return;

    const reordered = [...ch.lessons];
    const temp = reordered[idx];
    reordered[idx] = reordered[targetIdx];
    reordered[targetIdx] = temp;

    updateFormData({
      chapters: chapters.map(c => c.id === chapterId ? { ...c, lessons: reordered } : c)
    });
  };

  const handleUpdateChapterTitle = (chapterId: string, title: string) => {
    updateFormData({
      chapters: chapters.map(c => c.id === chapterId ? { ...c, title } : c)
    });
  };

  const handleUpdateLesson = (chapterId: string, lessonId: string, updated: Partial<Lesson>) => {
    updateFormData({
      chapters: chapters.map(ch =>
        ch.id === chapterId
          ? {
              ...ch,
              lessons: ch.lessons.map(l =>
                l.id === lessonId ? { ...l, ...updated } : l
              ),
            }
          : ch
      ),
    });
  };

  const isSubmitting = createCourseMutation.isPending;
  const thumbnailUploading = uploadThumbnailMutation.isPending;

  const goToStep = (next: number) => {
    if (next === 1 || next === 2) {
      const errs: Record<string, string> = {};
      if (!title.trim()) errs.title = "Course title is required";
      if (selectedLevels.length === 0) errs.levels = "Please select at least one level";
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }
    }
    setErrors({});
    setStep(next);
  };

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* Header */}
        <div className="dash-page-header mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-8 shrink-0" onClick={() => navigate("/admin/courses")}>
              <ArrowLeft className="size-4 mr-1" />
              Courses
            </Button>
            <div className="min-w-0">
              <h1 className="dash-page-title truncate">Create Course</h1>
              <p className="dash-page-subtitle truncate">
                Fill in the details to launch a new course.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* ─── Left: Stepper Sidebar ──────────────────────────────────── */}
          <div className="lg:w-56 shrink-0">

            <div className="space-y-1">
              {steps.map((s, i) => {
                const isCompleted = i < step;
                const isCurrent = i === step;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      if (i <= step) {
                        setStep(i);
                      }
                    }}
                    disabled={i > step}
                    className={cn(
                      "w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all duration-150",
                      isCurrent && "bg-primary/10",
                      !isCurrent && i <= step && "hover:bg-muted/50",
                      i > step && "opacity-40 cursor-default",
                    )}
                  >
                    {/* Step indicator */}
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold mt-0.5 transition-colors",
                      isCompleted && "bg-success text-white",
                      isCurrent && "bg-primary text-white shadow-md shadow-primary/30",
                      !isCompleted && !isCurrent && "bg-muted text-muted-foreground",
                    )}>
                      {isCompleted ? <CheckCircle2 size={14} /> : <span>{i + 1}</span>}
                    </div>
                    <div>
                      <p className={cn(
                        "text-sm font-semibold leading-tight",
                        isCurrent ? "text-primary" : isCompleted ? "text-success" : "text-muted-foreground",
                      )}>
                        {s.label}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{s.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Course Summary (step 2) */}
            {step === 2 && (
              <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/15 space-y-2">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Summary</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Chapters</span>
                    <span className="font-semibold text-foreground">{chapters.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lessons</span>
                    <span className="font-semibold text-foreground">{chapters.reduce((a, c) => a + c.lessons.length, 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-semibold text-foreground capitalize">{courseType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className={cn("font-semibold", published ? "text-success" : "text-amber-600")}>
                      {published ? "Will publish" : "Draft"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── Right: Form Content ────────────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* ── Step 1: Basic Info ─────────────────────────────────────── */}
            {step === 0 && (
              <KCard className="p-6 md:p-8 space-y-6">
                <div>
                  <h2 className="text-base font-semibold text-foreground mb-0.5">Course Details</h2>
                  <p className="text-sm text-muted-foreground">Set the foundation for your course.</p>
                </div>

                <div className="space-y-2">
                  <Label>Course Title <span className="text-destructive">*</span></Label>
                  <Input
                    placeholder="e.g., JLPT N3 Complete Mastery"
                    value={title}
                    onChange={(e) => {
                      updateFormData({ title: e.target.value });
                      if (errors.title) setErrors(prev => ({ ...prev, title: "" }));
                    }}
                    maxLength={255}
                    disabled={isSubmitting}
                    className={cn(errors.title && "border-destructive focus-visible:ring-destructive")}
                  />
                  {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <textarea
                    value={description}
                    onChange={(e) => updateFormData({ description: e.target.value })}
                    className="w-full h-28 px-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none focus:ring-2 focus:ring-ring transition-shadow"
                    placeholder="What will students learn in this course?"
                    maxLength={500}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Levels Selection */}
                <div className="space-y-4">
                  <div>
                    <Label>Levels <span className="text-destructive">*</span></Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Select all levels this course covers.</p>
                  </div>
                  
                  <div className="space-y-4">
                    {activeConfigs.map(config => {
                      if (!config.levels || config.levels.length === 0) return null;
                      return (
                        <div key={config.id} className="space-y-2 border border-border/40 bg-secondary/10 p-3 rounded-xl">
                          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{config.name} Levels</Label>
                          <div className="flex flex-wrap gap-2">
                            {config.levels.map((lvl) => (
                              <button
                                key={lvl.code}
                                type="button"
                                onClick={() => {
                                  const isDifferentConfig = selectedLevels.some(sl => !config.levels?.some(l => l.code === sl));
                                  let nextLevels;
                                  if (isDifferentConfig) {
                                    nextLevels = [lvl.code];
                                  } else {
                                    nextLevels = selectedLevels.includes(lvl.code)
                                      ? selectedLevels.filter((x) => x !== lvl.code)
                                      : [...selectedLevels, lvl.code];
                                  }
                                  updateFormData({
                                    selectedLevels: nextLevels
                                  });
                                  if (errors.levels) setErrors(prev => ({ ...prev, levels: "" }));
                                }}
                                disabled={isSubmitting}
                                className={cn(
                                  "flex items-center px-4 py-2.5 rounded-xl border text-xs font-medium transition-all duration-150",
                                  selectedLevels.includes(lvl.code)
                                    ? "border-primary bg-primary/10 text-primary shadow-sm"
                                    : "border-border text-muted-foreground hover:bg-muted/50 bg-background"
                                )}
                              >
                                <span className="font-bold text-sm">{lvl.code}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {errors.levels && <p className="text-xs text-destructive">{errors.levels}</p>}
                </div>

                {/* Course Type */}
                <div className="space-y-2">
                  <Label>Course Type</Label>
                  <div className="flex gap-3">
                    {(["recorded", "live"] as const).map(t => (
                      <button
                        key={t}
                        onClick={() => updateFormData({ courseType: t })}
                        className={cn(
                          "flex-1 h-11 rounded-xl border text-sm font-medium capitalize transition-all duration-150",
                          courseType === t
                            ? "border-primary bg-primary/10 text-primary shadow-sm"
                            : "border-border text-muted-foreground hover:bg-secondary"
                        )}
                        disabled={isSubmitting}
                      >
                        {t === "recorded" ? "🎬 Recorded" : "📡 Live Classes"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Thumbnail */}
                <div className="space-y-2">
                  <Label>Course Thumbnail</Label>
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-2xl p-8 flex flex-col items-center gap-3 cursor-pointer transition-all duration-150 relative overflow-hidden min-h-[140px]",
                      isDragging
                        ? "border-primary bg-primary/5 scale-[1.01]"
                        : "border-border hover:border-primary/50 hover:bg-muted/30"
                    )}
                    onClick={() => thumbnailInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) void onPickThumbnailFile(file);
                    }}
                  >
                    {(thumbnailPreviewUrl || thumbnailUrl) ? (
                      <img
                        src={thumbnailPreviewUrl ?? thumbnailUrl ?? ""}
                        alt="Course thumbnail preview"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Upload size={20} className="text-primary" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-foreground">Click to upload or drag & drop</p>
                          <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WebP — up to 2MB</p>
                        </div>
                      </>
                    )}

                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isSubmitting || thumbnailUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void onPickThumbnailFile(file);
                        e.currentTarget.value = "";
                      }}
                    />

                    {thumbnailUploading && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                        <p className="text-sm font-medium text-foreground animate-pulse">Uploading...</p>
                      </div>
                    )}

                    {thumbnailUrl && !thumbnailUploading && (
                      <div className="absolute bottom-2 right-2 z-10 bg-success text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        ✓ Uploaded
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={() => goToStep(1)} disabled={isSubmitting} className="gap-2">
                    Next: Structure
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </KCard>
            )}

            {/* ── Step 2: Structure ──────────────────────────────────────── */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="mb-2">
                  <h2 className="text-base font-semibold text-foreground mb-0.5">Course Structure</h2>
                  <p className="text-sm text-muted-foreground">Organize your content into chapters and lessons.</p>
                </div>

                {chapters.map((ch, idx) => (
                  <ChapterCard
                    key={ch.id}
                    chapter={ch}
                    index={idx}
                    isLast={idx === chapters.length - 1}
                    onDeleteChapter={() => removeChapter(ch.id)}
                    onMoveUp={() => handleMoveChapter(idx, "up")}
                    onMoveDown={() => handleMoveChapter(idx, "down")}
                    onUpdateTitle={(title) => handleUpdateChapterTitle(ch.id, title)}
                    onAddLesson={() => addLesson(ch.id)}
                    onDeleteLesson={(lessonId) => removeLesson(ch.id, lessonId)}
                    onUpdateLesson={(lessonId, updated) => handleUpdateLesson(ch.id, lessonId, updated)}
                    onMoveLessonUp={(lessonIdx) => handleMoveLesson(ch.id, lessonIdx, "up")}
                    onMoveLessonDown={(lessonIdx) => handleMoveLesson(ch.id, lessonIdx, "down")}
                  />
                ))}

                <Button variant="outline" onClick={addChapter} className="gap-2 w-full border-dashed h-11">
                  <Plus size={15} />
                  Add Chapter
                </Button>

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(0)} disabled={isSubmitting} className="gap-2">
                    <ArrowLeft size={15} />
                    Back
                  </Button>
                  <Button onClick={() => goToStep(2)} disabled={isSubmitting} className="gap-2">
                    Next: Pricing
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step 3: Pricing & Publish ──────────────────────────────── */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="mb-2">
                  <h2 className="text-base font-semibold text-foreground mb-0.5">Pricing & Publishing</h2>
                  <p className="text-sm text-muted-foreground">Set the price and decide when to go live.</p>
                </div>

                <KCard className="p-6 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price (BDT) <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground select-none">৳</span>
                        <Input
                          placeholder="5000"
                          className={cn("pl-8", errors.price && "border-destructive focus-visible:ring-destructive")}
                          value={price}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) {
                              updateFormData({ price: "" });
                              if (errors.price) setErrors(prev => ({ ...prev, price: "" }));
                              return;
                            }
                            const n = Number.parseFloat(v);
                            if (!Number.isNaN(n)) {
                              updateFormData({ price: n });
                              if (errors.price) setErrors(prev => ({ ...prev, price: "" }));
                            }
                          }}
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.price && <p className="text-xs text-destructive">{errors.price}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Discount Price <span className="text-muted-foreground text-xs">(coming soon)</span></Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground select-none">৳</span>
                        <Input placeholder="—" className="pl-8" disabled />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl border border-border/60">
                    <div>
                      <p className="font-medium text-foreground text-sm">Publish immediately</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Students can see and enroll in this course right away.</p>
                    </div>
                    <Switch
                      checked={published}
                      onCheckedChange={(val) => updateFormData({ published: val })}
                      disabled={isSubmitting}
                    />
                  </div>

                  {courseType === "live" && (
                    <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 rounded-xl border border-blue-100">
                      <Info size={15} className="text-blue-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700">
                        Live session links and schedules can be added after creation in the Course Builder.
                      </p>
                    </div>
                  )}
                </KCard>

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(1)} disabled={isSubmitting || thumbnailUploading} className="gap-2">
                    <ArrowLeft size={15} />
                    Back
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (price === "" || (typeof price === 'number' && price < 0) || Number.isNaN(Number(price))) {
                          setErrors(prev => ({ ...prev, price: "A valid non-negative price is required" }));
                          return;
                        }
                        setErrors({});
                        createCourseMutation.mutate(false);
                      }}
                      disabled={isSubmitting || thumbnailUploading}
                    >
                      {isSubmitting ? "Creating..." : "Save as Draft"}
                    </Button>
                    <Button
                      onClick={() => {
                        if (price === "" || (typeof price === 'number' && price < 0) || Number.isNaN(Number(price))) {
                          setErrors(prev => ({ ...prev, price: "A valid non-negative price is required" }));
                          return;
                        }
                        setErrors({});
                        createCourseMutation.mutate(published);
                      }}
                      disabled={isSubmitting || thumbnailUploading}
                      className="gap-2"
                    >
                      {isSubmitting ? "Creating..." : published ? "Create & Publish" : "Create Course"}
                      {!isSubmitting && <ChevronRight size={16} />}
                    </Button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
